/**
 * Step 3 of the Upload Wizard — Taxonomy version select.
 *
 * The user picks a Taxonomy version (required — defaults to live when
 * one exists, never falls back silently). If the collection's taxonomy
 * has no live version, the step renders a destructive Alert and the
 * parent wizard's `canAdvance` keeps the Next button blocked.
 *
 * The "Eval Mode" preset that used to live here was scaffolding for an
 * evaluation pipeline that never landed (no service consumed it; see
 * the `20260601_golab_drop_eval_preset` migration that dropped the
 * audit-row column). When the pipeline gets built, the picker can come
 * back — with prompt selection alongside, on a sane per-dimension
 * schema. Prompts are intentionally not surfaced here today for the
 * same reason.
 */

import React, { useEffect } from 'react';
import { AlertTriangle } from 'lucide-react';

import { Alert, AlertDescription, AlertTitle } from '../../../../ui/alert';
import { Label } from '../../../../ui/label';
import {
  Select,
  SelectContent,
  SelectItem,
  SelectTrigger,
  SelectValue,
} from '../../../../ui/select';
import { useVersionList } from '../../hooks/useTaxonomy';
import type { Collection } from '../types';

interface Props {
  collection: Collection;
  taxonomyVersionId: string;
  onTaxonomyVersionIdChange: (id: string) => void;
}

const Step3Config: React.FC<Props> = ({
  collection,
  taxonomyVersionId,
  onTaxonomyVersionIdChange,
}) => {
  const { data: taxonomyVersions } = useVersionList(collection.taxonomyKey);

  // Default to the live version once data arrives (only when nothing's
  // selected yet — preserves user picks if they already chose).
  useEffect(() => {
    if (taxonomyVersionId || !taxonomyVersions) return;
    const live = taxonomyVersions.find((v) => v.status === 'live');
    if (live) onTaxonomyVersionIdChange(live.id);
  }, [taxonomyVersions, taxonomyVersionId, onTaxonomyVersionIdChange]);

  // Drop drafts from the taxonomy options — the wizard only references
  // live + archived for repeatable evaluation.
  const taxonomyOptions = (taxonomyVersions ?? []).filter(
    (v) => v.status !== 'draft',
  );

  // A live taxonomy version is required. When missing, surface an
  // explicit error; the parent's `canAdvance` blocks Next via the
  // empty `taxonomyVersionId`.
  const liveTaxonomyMissing =
    taxonomyVersions !== undefined &&
    !taxonomyVersions.some((v) => v.status === 'live');

  return (
    <div className="space-y-6" data-testid="step3-config">
      {liveTaxonomyMissing && (
        <Alert variant="destructive" data-testid="step3-missing-live-version">
          <AlertTriangle className="h-4 w-4" aria-hidden="true" />
          <AlertTitle>Cannot proceed</AlertTitle>
          <AlertDescription>
            No live taxonomy version exists for &quot;{collection.taxonomyKey}&quot;.
            Promote a taxonomy version to live in the registry before
            uploading.
          </AlertDescription>
        </Alert>
      )}
      <div className="space-y-2">
        <Label className="text-[12px] font-semibold uppercase tracking-wide text-muted-foreground">
          Taxonomy Version
        </Label>
        <Select
          value={taxonomyVersionId}
          onValueChange={onTaxonomyVersionIdChange}
        >
          <SelectTrigger data-testid="step3-taxonomy-trigger">
            <SelectValue placeholder="Select taxonomy version" />
          </SelectTrigger>
          <SelectContent>
            {taxonomyOptions.map((v) => (
              <SelectItem key={v.id} value={v.id}>
                v{v.versionNumber} ({v.status})
                {v.labelCount != null && ` · ${v.labelCount} labels`}
              </SelectItem>
            ))}
          </SelectContent>
        </Select>
      </div>
    </div>
  );
};

export default Step3Config;
